import React, { useMemo, useState } from 'react';
import {
  Button,
  ButtonVariant,
  FlyoutContainer,
  ComboboxOption,
  ComboBox,
  LargeFormFieldPresentational,
  CmhcIcon,
  CmhcIconSize
} from '@cmhc-schl/hearth-ui';
import { Box, Stack, Typography } from '@mui/material';
import { useAuthenticationContext } from 'Foundation/Authentication';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { MasterPoolNumberAndRemarksTable } from './MasterPoolNumberAndRemarksTable';
import styles from './styles.module.scss';
import { useDecisionOverrideRationales } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtIndividualLoanDetails/hooks/useDecisionOverrideRationales';
import { Remark, MasterPoolNumberAddRemarksProps } from '../../types';
import { useMasterPoolAddRemarksMutation } from './api/hooks/useMutations';
import {
  OTHER_REMARK_CODE,
  POOL_SUBMISSION_PACKAGE_CODES,
  OFFER_EMAIL_CODES,
  CODES_FOR_REMARK_REVIEWR_MESSAGE
} from 'Feature/V2/HbtMasterPool/constants';

const FORMFIELDMAXCHAR = 300;
const MASTERPOOL_ADD_REMARS_CODES = [
  ...POOL_SUBMISSION_PACKAGE_CODES,
  ...OFFER_EMAIL_CODES,
  OTHER_REMARK_CODE
];

const HbtMasterPoolNumberAddRemarksFlyout = ({
  masterPoolNumber,
  latestRemark,
  openFlyout,
  toggleDrawer,
  toggleToaster
}: MasterPoolNumberAddRemarksProps) => {
  const { t } = useTranslation('HbtMasterPool');
  const authenticationContext = useAuthenticationContext();
  const [remark, setRemark] = useState<Remark>({ remarkID: undefined, remarkMessage: undefined });
  const [rationaleRequired, setRationaleRequired] = useState<boolean>(false);
  const [largeFormFieldRequired, setLargeFormFieldRequired] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { data: decisionOverrideData } = useDecisionOverrideRationales(authenticationContext);
  const [rationaleInput, setRationaleInput] = useState<string>('');

  const rationaleOptions: ComboboxOption[] = useMemo(() => {
    if (!decisionOverrideData) return [];
    const options: ComboboxOption[] = [];

    const dropdownOptions = decisionOverrideData.filter((item: any) =>
      MASTERPOOL_ADD_REMARS_CODES.includes(item.id)
    );

    dropdownOptions?.forEach((r: any) => {
      const label = i18n.language === 'en' ? r.englishRationale : r.frenchRationale;
      let option: ComboboxOption = {
        value: `${r.id}`,
        label,
        isGroup: true,
        groupName: ''
      };

      switch (true) {
        case POOL_SUBMISSION_PACKAGE_CODES.includes(r.id):
          option = {
            ...option,
            groupName: t(
              'Masterpool-Flyout-Add-Remark-Dropdown-Option-Header-Pool-Submission-Package'
            )
          };
          break;

        case OFFER_EMAIL_CODES.includes(r.id):
          option = {
            ...option,
            groupName: t(
              'Masterpool-Flyout-Add-Remark-Dropdown-Option-Header-Letter-Of-Offer-Email'
            )
          };
          break;

        case r.id === OTHER_REMARK_CODE:
          option = {
            ...option,
            groupName: t('Masterpool-Flyout-Add-Remark-Dropdown-Option-Header-Other')
          };
          break;

        default:
          option = {
            ...option,
            groupName: t('Masterpool-Flyout-Add-Remark-Dropdown-Option-Header-Other')
          };
      }

      options.push(option);
    });
    return options;
  }, [decisionOverrideData, i18n.language]);

  const latestRemarkWithTranslations = useMemo(() => {
    if (!decisionOverrideData || !latestRemark) return;

    if (latestRemark.remarkId === 9999) {
      return latestRemark;
    }

    const match = decisionOverrideData.find(
      (rationale: any) => rationale.id === latestRemark.remarkId
    );
    return {
      ...latestRemark,
      remarkText: i18n.language === 'en' ? match?.englishRationale : match?.frenchRationale
    };
  }, [decisionOverrideData, latestRemark, i18n.language]);

  const handleRemarkChange = (remarks: ComboboxOption | null) => {
    if (remarks) {
      setRemark({
        remarkMessage: +remarks.value !== OTHER_REMARK_CODE ? remarks.label : undefined,
        remarkID: +remarks.value
      });

      if (rationaleRequired) {
        setRationaleRequired(false);
      }
      return;
    }

    setRemark({
      remarkMessage: undefined,
      remarkID: undefined
    });
  };

  const { mutateAsync: overrideMasterPoolRemarks } = useMasterPoolAddRemarksMutation();

  const handleLargeFormField = (value: string) => {
    setRemark({ ...remark, remarkMessage: value });
    setRationaleInput(value);
    if (value.length > FORMFIELDMAXCHAR) {
      setLargeFormFieldRequired(true);
    } else if (largeFormFieldRequired && value.length <= FORMFIELDMAXCHAR) {
      setLargeFormFieldRequired(false);
    }
  };

  const closeFlyout = (isErrorFlag?: boolean) => {
    setRemark({ remarkMessage: undefined, remarkID: undefined });
    setLargeFormFieldRequired(false);
    toggleDrawer(false);
    setRationaleRequired(false);
    setRationaleInput('');
    setIsSubmitting(false);
    if (isErrorFlag !== undefined) {
      isErrorFlag
        ? toggleToaster(true, {
            header: t('Masterpool-Flyout-Add-Remarks-Not-Successful-Header'),
            subHeader: t('Masterpool-Flyout-Add-Remarks-Not-Successful-Body')
          })
        : toggleToaster(false, {
            header: t('Masterpool-Flyout-Add-Remarks-Successful-Header'),
            subHeader: t('Masterpool-Flyout-Add-Remarks-Successful-Body')
          });
    }
  };

  const handleSubmit = () => {
    if (!remark.remarkID) {
      setRationaleRequired(true);
      return;
    }
    if (
      (CODES_FOR_REMARK_REVIEWR_MESSAGE.includes(remark?.remarkID) && !rationaleInput) ||
      (remark.remarkMessage && rationaleInput.length > FORMFIELDMAXCHAR)
    ) {
      setLargeFormFieldRequired(true);
      return;
    }

    const payload = {
      remark: {
        remarkId: remark.remarkID,
        remarkText: remark.remarkMessage
      }
    };

    setIsSubmitting(true);

    masterPoolNumber &&
      overrideMasterPoolRemarks({
        authenticationContext,
        masterPoolNumber,
        payload
      })
        .then(() => {
          setIsSubmitting(false);
          closeFlyout(false);
        })
        .catch(() => {
          setIsSubmitting(false);
          closeFlyout(true);
        });
  };

  return (
    <Box>
      <FlyoutContainer
        id={styles.portfolioFilterFlyoutContainer}
        isHeaderRequired={true}
        headerText={t('Masterpool-Flyout-Add-Remark')}
        open={openFlyout}
        onClose={() => closeFlyout()}
        content={
          <Box
            className={`${styles.flyoutContainer} ${styles.flyoutMainContainer} ${styles.modifyRemarkFlyout}`}
            role="presentation"
          >
            <Box className={styles.flyoutContent}>
              <Box className={styles.flyoutWrapper}>
                <Typography className={styles.flyoutDesc} variant="h6">
                  {t('Masterpool-Flyout-Add-Remark-Header-Help-Text')}
                </Typography>
                {masterPoolNumber !== null && (
                  <MasterPoolNumberAndRemarksTable
                    masterPoolNumber={masterPoolNumber}
                    latestRemark={latestRemarkWithTranslations?.remarkText ?? ''}
                  />
                )}
              </Box>
              <Box className={`${styles.flyoutWrapper} ${styles.remarks}`}>
                <Box className={styles.dropdownHeaderWrapper}>
                  <Typography className={styles.remarksHeader}>
                    {t('Masterpool-Flyout-Add-Remark-Sub-Header')}
                  </Typography>
                  <Typography className={styles.flyoutSubHeaderDesc}>
                    {t('Masterpool-Flyout-Add-Remark-Sub-Header-desc')}
                  </Typography>
                </Box>
                <Box className={styles.remarksDropDown}>
                  <Typography className={styles.title}>
                    {t('Masterpool-Flyout-Add-Remark-Column-Header-2')}
                  </Typography>
                  <Box className={styles.actionsDropdown}>
                    <ComboBox
                      id="masterpool-remarks-combobox"
                      ariaText={t('Masterpool-Flyout-Add-Remark-Dropdown-Select')}
                      className={styles.modifyRemarksOptions}
                      options={rationaleOptions}
                      onChange={(e, v) => handleRemarkChange(v)}
                      error={rationaleRequired}
                      errorMessage={t('Masterpool-Flyout-Add-Remark-Dropdown-Field-Required-Error')}
                      placeholderText={t('Masterpool-Flyout-Add-Remark-Dropdown-Select')}
                    />
                  </Box>
                </Box>
                {remark.remarkID != OTHER_REMARK_CODE && (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: 'start',
                      alignItems: 'start'
                    }}
                    className={styles.remarkAddedContainer}
                  >
                    <CmhcIcon
                      className={styles.remarkIcon}
                      size={CmhcIconSize.MEDIUM}
                      type={'icon_info_outlined'}
                    />
                    <Stack direction="row" spacing={1} className={styles.remarksAdded}>
                      <Typography>
                        <span className={styles.title}>{t('Add-Remarks-Reviewer')}</span>
                      </Typography>
                    </Stack>
                  </Stack>
                )}
                {remark?.remarkID &&
                  CODES_FOR_REMARK_REVIEWR_MESSAGE.includes(remark?.remarkID) && (
                    <Box>
                      <LargeFormFieldPresentational
                        id={'large-form-field-pricing-scenario'}
                        ariaText={
                          rationaleInput.length > 0
                            ? rationaleInput
                            : t('Masterpool-Flyout-Add-Remark-Column-Header-2')
                        }
                        value={rationaleInput}
                        invalid={largeFormFieldRequired}
                        error={
                          largeFormFieldRequired
                            ? rationaleInput.length > FORMFIELDMAXCHAR
                              ? t('Masterpool-Flyout-Add-Remark-Large-Input-Limit-Exceed-Error')
                              : t('Masterpool-Flyout-Add-Remark-Large-Input-No-Message-Error')
                            : ''
                        }
                        maxLength={FORMFIELDMAXCHAR}
                        onChange={(e) => handleLargeFormField(e.target.value)}
                      />
                    </Box>
                  )}
              </Box>
            </Box>

            <Box className={styles.flyoutFooter}>
              <Stack direction="row" spacing={3}>
                <Box width="100%">
                  <Button
                    type="button"
                    name="btn"
                    onClick={() => closeFlyout()}
                    className={styles.fullWidth}
                    variant={ButtonVariant.SECONDARY}
                    text={{
                      value: t('Cancel')
                    }}
                    ariaText={{
                      value: t('Cancel')
                    }}
                    role="button"
                  />
                </Box>
                <Box width="100%">
                  <Button
                    type="submit"
                    name="btn"
                    onClick={handleSubmit}
                    className={styles.submitButton}
                    variant={ButtonVariant.PRIMARY}
                    loading={isSubmitting}
                    text={{
                      value: isSubmitting
                        ? t('Masterpool-Flyout-Add-Remarks-Loading-Button')
                        : t('Masterpool-Flyout-Add-Remarks-Button')
                    }}
                    ariaText={{
                      value: isSubmitting
                        ? t('Masterpool-Flyout-Add-Remarks-Loading-Button')
                        : t('Masterpool-Flyout-Add-Remarks-Button')
                    }}
                    role="button"
                  />
                </Box>
              </Stack>
            </Box>
          </Box>
        }
      />
    </Box>
  );
};

export default HbtMasterPoolNumberAddRemarksFlyout;
